import React, { useState, useRef, useCallback } from 'react';
import { useStyletron } from 'baseui';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Notification, KIND } from 'baseui/notification';
import { CURRENT_ENV, ENVIRONMENT } from 'commons-ui';
import { aiCompanionApi } from './api';
import GuardrailReviewPanel from './GuardrailReviewPanel';

type ActionType = 'deleteProfile' | 'updateTalktime' | 'blockUser' | 'unblockUser' | 'guardrailReview' | null;
type BulkMode = 'single' | 'bulk';

const isProduction: boolean = CURRENT_ENV === ENVIRONMENT.PRODUCTION;

interface NotificationState {
	message: string;
	kind: typeof KIND[keyof typeof KIND];
}

const ALL_ACTIONS = [
	{
		id: 'deleteProfile' as ActionType,
		title: 'Delete User Profile',
		description: "Permanently delete a user's AI companion profile and chat history"
	},
	{
		id: 'updateTalktime' as ActionType,
		title: 'Update Talktime',
		description: 'Set paid talktime limit and subscription daily time limit for a user',
		stagingOnly: true
	},
	{
		id: 'blockUser' as ActionType,
		title: 'Block User',
		description: 'Block a user from AI companion',
		hasBulkMode: true
	},
	{
		id: 'unblockUser' as ActionType,
		title: 'Unblock User',
		description: 'Remove all active blocks for a user from AI companion',
		hasBulkMode: true
	},
	{
		id: 'guardrailReview' as ActionType,
		title: 'Guardrail Review',
		description: 'Browse guardrail enforcement actions (blocks, warnings, flagged-for-review)'
	}
];

const ACTIONS = ALL_ACTIONS.filter((a) => !a.stagingOnly || !isProduction);

const labelStyle = {
	fontSize: '13px',
	fontWeight: '500' as const,
	color: '#374151',
	display: 'block',
	marginBottom: '6px'
};

const parseCsvUserIds = (text: string): string[] => {
	return text
		.split(/[\n,]+/)
		.map((id) => id.trim())
		.filter((id) => id.length > 0);
};

const AiCompanionDashboard: React.FC = () => {
	const [css] = useStyletron();
	const [activeAction, setActiveAction] = useState<ActionType>('deleteProfile');
	const [bulkMode, setBulkMode] = useState<BulkMode>('single');
	const [notification, setNotification] = useState<NotificationState | null>(null);
	const [loading, setLoading] = useState(false);

	const [userId, setUserId] = useState('');
	const [bulkRows, setBulkRows] = useState<{ id: string; value: string }[]>([{ id: 'r0', value: '' }]);
	const rowCounter = useRef(1);
	const [talktimeMinutes, setTalktimeMinutes] = useState('');
	const [subscriptionMinutes, setSubscriptionMinutes] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	const makeRowId = useCallback(() => {
		const id = `r${rowCounter.current}`;
		rowCounter.current += 1;
		return id;
	}, []);

	const resetForm = () => {
		setUserId('');
		setBulkRows([{ id: makeRowId(), value: '' }]);
		setTalktimeMinutes('');
		setSubscriptionMinutes('');
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleBulkRowChange = (rowId: string, value: string) => {
		setBulkRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, value } : r)));
	};

	const addBulkRow = () => {
		setBulkRows((prev) => [...prev, { id: makeRowId(), value: '' }]);
	};

	const removeBulkRow = (rowId: string) => {
		setBulkRows((prev) => {
			if (prev.length <= 1) return prev;
			return prev.filter((r) => r.id !== rowId);
		});
	};

	const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (event) => {
			const text = event.target?.result as string;
			const ids = parseCsvUserIds(text);
			if (ids.length > 0) {
				setBulkRows(ids.map((val) => ({ id: makeRowId(), value: val })));
				setNotification({
					message: `Loaded ${ids.length} user ID(s) from CSV`,
					kind: KIND.positive
				});
			} else {
				setNotification({ message: 'No valid user IDs found in CSV', kind: KIND.negative });
			}
		};
		reader.readAsText(file);
	};

	const getBulkIds = (): string[] => {
		return bulkRows.map((r) => r.value.trim()).filter((v) => v.length > 0);
	};

	const handleNavClick = (id: ActionType) => {
		setActiveAction(id);
		setBulkMode('single');
		setNotification(null);
		resetForm();
	};

	const handleSubmit = async () => {
		const isBulk = bulkMode === 'bulk';

		if (isBulk) {
			const ids = getBulkIds();
			if (ids.length === 0) {
				setNotification({ message: 'Please enter at least one User ID', kind: KIND.negative });
				return;
			}
			setLoading(true);
			setNotification(null);
			try {
				let result;
				if (activeAction === 'blockUser') {
					result = await aiCompanionApi.bulkBlockUsers(ids);
				} else if (activeAction === 'unblockUser') {
					result = await aiCompanionApi.bulkUnblockUsers(ids);
				}
				if (result?.success) {
					setNotification({
						message: result.data?.message || `Action completed for ${ids.length} user(s)`,
						kind: KIND.positive
					});
					resetForm();
				} else {
					setNotification({ message: result?.error || 'Action failed', kind: KIND.negative });
				}
			} catch (e) {
				setNotification({ message: 'Network error. Please try again.', kind: KIND.negative });
			}
			setLoading(false);
			return;
		}

		if (!userId.trim()) {
			setNotification({ message: 'User ID is required', kind: KIND.negative });
			return;
		}
		setLoading(true);
		setNotification(null);
		try {
			let result;
			if (activeAction === 'deleteProfile') {
				result = await aiCompanionApi.deleteUserProfile(userId);
			} else if (activeAction === 'updateTalktime') {
				if (!talktimeMinutes) {
					setNotification({ message: 'Paid Talktime is required', kind: KIND.negative });
					setLoading(false);
					return;
				}
				result = await aiCompanionApi.updateTalktime(
					Number(userId),
					Number(talktimeMinutes),
					subscriptionMinutes ? Number(subscriptionMinutes) : 0
				);
			} else if (activeAction === 'blockUser') {
				result = await aiCompanionApi.blockUser(userId);
			} else if (activeAction === 'unblockUser') {
				result = await aiCompanionApi.unblockUser(userId);
			}

			if (result?.success) {
				setNotification({ message: result.data?.message || 'Action completed successfully', kind: KIND.positive });
				resetForm();
			} else {
				setNotification({ message: result?.error || 'Action failed', kind: KIND.negative });
			}
		} catch (e) {
			setNotification({ message: 'Network error. Please try again.', kind: KIND.negative });
		}
		setLoading(false);
	};

	const activeItem = ACTIONS.find((a) => a.id === activeAction);
	const hasBulkMode = activeItem?.hasBulkMode === true;

	return (
		<div className={css({ display: 'flex', height: '100%', backgroundColor: '#f8f9fa' })}>
			{/* Sidebar */}
			<div
				className={css({
					width: '260px',
					minWidth: '260px',
					backgroundColor: '#fff',
					borderRight: '1px solid #e5e7eb',
					padding: '24px 0',
					display: 'flex',
					flexDirection: 'column'
				})}>
				<div className={css({ padding: '0 20px 20px', borderBottom: '1px solid #e5e7eb' })}>
					<h2 className={css({ fontSize: '15px', fontWeight: '700', color: '#111', margin: '0' })}>AI Companion Admin</h2>
					<p className={css({ fontSize: '12px', color: '#9ca3af', marginTop: '4px', marginBottom: '0' })}>
						Select an action
					</p>
				</div>

				<nav className={css({ padding: '12px 0' })}>
					{ACTIONS.map((action) => {
						const isActive = activeAction === action.id;
						return (
							<div
								key={action.id}
								role='button'
								tabIndex={0}
								onClick={() => handleNavClick(action.id)}
								className={css({
									padding: '12px 20px',
									cursor: 'pointer',
									backgroundColor: isActive ? '#f0f4ff' : 'transparent',
									borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent',
									':hover': { backgroundColor: '#f9fafb' }
								})}>
								<div
									className={css({
										fontSize: '13px',
										fontWeight: isActive ? '600' : '400',
										color: isActive ? '#4f46e5' : '#374151'
									})}>
									{action.title}
								</div>
							</div>
						);
					})}
				</nav>
			</div>

			{/* Main Content */}
			<div className={css({ flex: '1', padding: '36px 40px', overflowY: 'auto' })}>
				{activeAction === 'guardrailReview' && <GuardrailReviewPanel />}
				{activeAction !== 'guardrailReview' && activeItem && (
					<>
						<h3 className={css({ fontSize: '20px', fontWeight: '600', color: '#111', marginBottom: '6px' })}>
							{activeItem.title}
						</h3>
						<p className={css({ fontSize: '14px', color: '#6b7280', marginBottom: '28px' })}>{activeItem.description}</p>

						{hasBulkMode && (
							<div className={css({ display: 'flex', gap: '8px', marginBottom: '20px' })}>
								<Button
									size='compact'
									kind={bulkMode === 'single' ? 'primary' : 'secondary'}
									onClick={() => {
										setBulkMode('single');
										setNotification(null);
										resetForm();
									}}>
									{activeAction === 'blockUser' ? 'Block Single User' : 'Unblock Single User'}
								</Button>
								<Button
									size='compact'
									kind={bulkMode === 'bulk' ? 'primary' : 'secondary'}
									onClick={() => {
										setBulkMode('bulk');
										setNotification(null);
										resetForm();
									}}>
									{activeAction === 'blockUser' ? 'Block Multiple Users' : 'Unblock Multiple Users'}
								</Button>
							</div>
						)}

						{notification && (
							<div className={css({ marginBottom: '24px', maxWidth: '480px' })}>
								<Notification kind={notification.kind} onClose={() => setNotification(null)}>
									{notification.message}
								</Notification>
							</div>
						)}

						<div
							className={css({
								backgroundColor: '#fff',
								border: '1px solid #e5e7eb',
								borderRadius: '12px',
								padding: '28px',
								maxWidth: '480px'
							})}>
							<div className={css({ display: 'flex', flexDirection: 'column', gap: '16px' })}>
								{hasBulkMode && bulkMode === 'bulk' ? (
									<div className={css({ display: 'flex', flexDirection: 'column', gap: '12px' })}>
										<label className={css(labelStyle)}>User IDs *</label>
										{bulkRows.map((row) => (
											<div key={row.id} className={css({ display: 'flex', gap: '8px', alignItems: 'center' })}>
												<Input
													value={row.value}
													onChange={(e) => handleBulkRowChange(row.id, e.currentTarget.value)}
													placeholder='Enter user ID'
												/>
												{bulkRows.length > 1 && (
													<Button size='compact' kind='tertiary' onClick={() => removeBulkRow(row.id)}>
														X
													</Button>
												)}
											</div>
										))}
										<div>
											<Button size='compact' kind='secondary' onClick={addBulkRow}>
												+ Add Row
											</Button>
										</div>
										<div
											className={css({
												borderTop: '1px solid #e5e7eb',
												paddingTop: '12px',
												marginTop: '4px'
											})}>
											<label className={css(labelStyle)}>Or upload a CSV file</label>
											<p
												className={css({
													fontSize: '12px',
													color: '#6b7280',
													marginTop: '0',
													marginBottom: '8px'
												})}>
												CSV should have one zupeeUserId per line
											</p>
											<input ref={fileInputRef} type='file' accept='.csv' onChange={handleCsvUpload} />
										</div>
										<p
											className={css({
												fontSize: '12px',
												color: '#6b7280',
												marginTop: '0',
												marginBottom: '0'
											})}>
											{getBulkIds().length} user ID(s) entered
										</p>
									</div>
								) : (
									<>
										<div>
											<label className={css(labelStyle)}>User ID *</label>
											<Input
												value={userId}
												onChange={(e) => setUserId(e.currentTarget.value)}
												placeholder='Enter user ID'
											/>
										</div>

										{activeAction === 'updateTalktime' && (
											<>
												<div>
													<label className={css(labelStyle)}>Paid Talktime (minutes) *</label>
													<Input
														value={talktimeMinutes}
														onChange={(e) => setTalktimeMinutes(e.currentTarget.value)}
														placeholder='e.g. 60'
														type='number'
													/>
												</div>
												<div>
													<label className={css(labelStyle)}>Subscription Daily Limit (minutes)</label>
													<Input
														value={subscriptionMinutes}
														onChange={(e) => setSubscriptionMinutes(e.currentTarget.value)}
														placeholder='e.g. 30'
														type='number'
													/>
												</div>
											</>
										)}
									</>
								)}
								<div className={css({ marginTop: '4px' })}>
									<Button onClick={handleSubmit} isLoading={loading}>
										Submit
									</Button>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default AiCompanionDashboard;
